import React, { useEffect, useRef, useState } from "react";
import { BeatLoader } from "react-spinners";
import axios from "axios";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import tokenService from "../../../../utils/tokenService";
import { DEFAULT_AVATAR } from "../../../../utils/fallbackImages";

export function LoginSystem() {
  const [isUploading, setIsUploading] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isQuickLoggingIn, setIsQuickLoggingIn] = useState(false);
  // เครื่องนี้ยังถูก "จำ" ไว้อยู่ไหม → ใช้ตัดสินว่าข้อความบนการ์ดควรบอกว่าอะไร
  const [canSkipPassword, setCanSkipPassword] = useState(() => Boolean(tokenService.getRememberToken()));
  const passwordInputRef = useRef(null);

  // บัญชีที่เครื่องนี้จำไว้ (แค่ชื่อกับรูป ไม่มีรหัสผ่าน)
  const [savedAccount, setSavedAccount] = useState(() => {
    const name = localStorage.getItem("194aa3da_username");
    const image = localStorage.getItem("profile_user");
    return name && image ? { name, image } : null;
  });


  function shouldRedirectBack() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("redirectBack") === "true";
  }

  function handlePostLoginRedirect() {
    try {
      if (shouldRedirectBack()) {
        window.close();
      } else {
        if (window.location.replace) {
          window.location.replace("/home");
        } else {
          window.location.href = "/home";
        }
      }
    } catch (error) {
      setTimeout(() => { window.location = "/home"; }, 100);
    }
  }

  function handleValidatePermission(e) {
    setIsUploading(true);
    e.preventDefault();
    tokenService.clearIntentionalLogout();

    axios
      .post(`${process.env.REACT_APP_API}/auth/login`, {
        uname: username.trim(),
        password: password,
        // ขอ remember token ไว้ให้ครั้งหน้ากด "เข้าสู่ระบบด่วน" ได้เลย
        remember: true,
      })
      .then((res) => {
        const uidOfUser = res.data.uid;
        const token = res.data.token;

        if (!token) {
          setIsUploading(false);
          Swal.fire("เกิดข้อผิดพลาด!", "ไม่ได้รับ Token จากระบบ", "error");
          return;
        }

        tokenService.setToken(token, uidOfUser);
        tokenService.setRememberToken(res.data.remember_token);
        localStorage.setItem("194aa3da_username", username);

        setTimeout(() => {
          setIsUploading(false);
          handlePostLoginRedirect();
        }, 2000);
      })
      .catch((err) => {
        setIsUploading(false);
        if (err.response?.data?.Error) {
          Swal.fire("เกิดข้อผิดพลาด!", `${err.response.data.Error}`, "warning");
        } else if (err.message) {
          Swal.fire("เกิดข้อผิดพลาด!", `${err.message}`, "warning");
        } else {
          Swal.fire("เกิดข้อผิดพลาด!", "ไม่สามารถเข้าสู่ระบบได้ กรุณาลองใหม่", "warning");
        }
      });
  }

  // ถ้ายังมี session ที่ใช้ได้อยู่ ไม่ต้องให้ผู้ใช้กรอกรหัสผ่านซ้ำ พาเข้าหน้าหลักเลย
  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (tokenService.getToken()) {
        const status = await tokenService.checkSession();
        if (cancelled) return;

        if (status === "valid") {
          handlePostLoginRedirect();
          return;
        }
      }

      // เพิ่งกด "ออกจากระบบ" มา ห้ามพากลับเข้าไปเอง
      // (กันไว้เผื่อมีโค้ดไหนล้าง token ไม่ครบจน remember token ค้างอยู่)
      if (tokenService.wasIntentionalLogout()) return;

      // session หมดแล้วแต่เครื่องนี้ยังถูกจำไว้ → ต่อ session ให้เงียบ ๆ
      if (!tokenService.getRememberToken()) return;

      setIsQuickLoggingIn(true);
      const result = await tokenService.loginWithRememberToken();
      if (cancelled) return;

      if (result === "ok") {
        handlePostLoginRedirect();
        return;
      }
      setCanSkipPassword(false);
      setIsQuickLoggingIn(false);
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // เลิกจำเครื่องนี้จริง ๆ — ทางออกสำหรับเครื่องที่ใช้ร่วมกับคนอื่น
  // ต่างจากการกด "ออกจากระบบ" ตรงที่อันนี้เพิกถอน remember token ที่ server ด้วย
  // หลังกดแล้วจะแตะเข้าระบบด่วนไม่ได้อีก ต้องกรอกรหัสผ่านใหม่
  async function handleForgetAccount(e) {
    e.preventDefault();
    e.stopPropagation();

    // อัปเดตหน้าจอทันที ไม่ต้องรอ server
    setSavedAccount(null);
    setCanSkipPassword(false);
    setUsername("");

    localStorage.removeItem("194aa3da_username");
    localStorage.removeItem("profile_user");

    await tokenService.forgetDevice();
  }

  // การ์ด "เข้าสู่ระบบด่วน" — กดครั้งเดียวเข้าระบบเลย ไม่ต้องกรอกรหัสผ่าน
  //  1) session ยังไม่หมดอายุ → เข้าหน้าหลักได้เลย
  //  2) session หมดแล้ว แต่เครื่องนี้มี remember token → แลกเป็น session ใหม่
  //  3) ไม่มีทั้งคู่ (เพิ่งกดออกจากระบบ / ครบ 90 วัน / โดนเพิกถอน)
  //     → เติมชื่อผู้ใช้ให้ แล้วโฟกัสช่องรหัสผ่าน ให้ Face ID / iCloud Keychain
  //       เติมรหัสผ่านให้เอง — เราไม่เก็บรหัสผ่านไว้ในเครื่องเองเด็ดขาด
  async function handleQuickLogin(e) {
    e.preventDefault();

    if (!savedAccount) return;

    setUsername(savedAccount.name);
    setIsQuickLoggingIn(true);
    tokenService.clearIntentionalLogout();

    try {
      if (tokenService.getToken() && (await tokenService.checkSession()) === "valid") {
        handlePostLoginRedirect();
        return;
      }

      const result = await tokenService.loginWithRememberToken();

      if (result === "ok") {
        handlePostLoginRedirect();
        return;
      }

      if (result === "unreachable") {
        Swal.fire("เชื่อมต่อไม่ได้", "กรุณาตรวจสอบอินเทอร์เน็ตแล้วลองใหม่อีกครั้ง", "warning");
        return;
      }

      // result === 'invalid' หรือ 'none' → ต้องกรอกรหัสผ่านครั้งนี้
      setCanSkipPassword(false);
      passwordInputRef.current?.focus();
    } finally {
      setIsQuickLoggingIn(false);
    }
  }

  return (
    <main className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <motion.div
        className="w-full max-w-4xl"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        <div className="flex flex-col lg:flex-row gap-4">

          {/* ── LEFT: Branding card ── */}
          <div className="lg:w-[45%] bg-white rounded-2xl shadow-sm border border-gray-100 p-8 flex flex-col justify-between">

            {/* Logo + name */}
            <div>
              <div className="flex items-center gap-2.5 mb-6">
                <img
                  src="/favicon.ico"
                  alt="Postperty"
                  className="w-9 h-9 rounded-xl"
                  onError={(e) => { e.target.style.display = "none"; }}
                />
                <span className="text-xl font-bold text-gray-900 tracking-tight">Postperty</span>
              </div>

              <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">แพลตฟอร์มอสังหาริมทรัพย์</p>
              <h1 className="text-2xl font-bold text-gray-900 leading-snug mb-4">
                ซื้อ ขาย เช่า<br />อสังหาฯ บนแผนที่
              </h1>

              <div className="space-y-2.5 mt-6">
                {[
                  { icon: "https://cdn-icons-png.flaticon.com/512/854/854878.png", text: "ดูบ้านได้แบบ VR 3D" },
                  { icon: "https://cdn-icons-png.flaticon.com/512/2099/2099122.png", text: "ช่วยแชร์ รับค่าคอมมิชชั่น" },
                  { icon: "https://cdn-icons-png.flaticon.com/512/9706/9706624.png", text: "ทรัพย์ครบ ทั่วประเทศไทย" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-3 bg-gray-50 rounded-xl border border-gray-100">
                    <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm border border-gray-100">
                      <img src={item.icon} alt="" className="w-4 h-4" />
                    </div>
                    <span className="text-sm text-gray-600 font-medium">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick login (saved user) */}
            {savedAccount && (
              <div className="mt-8 pt-6 border-t border-gray-100">
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-3">เข้าสู่ระบบด่วน</p>
                <button
                  type="button"
                  onClick={handleQuickLogin}
                  disabled={isUploading || isQuickLoggingIn}
                  className="w-full flex items-center gap-3 px-4 py-3 bg-gray-50 rounded-xl border border-gray-100 hover:bg-gray-100 active:bg-gray-100 transition-colors text-left"
                >
                  <img
                    src={savedAccount.image}
                    className="w-10 h-10 rounded-full object-cover ring-2 ring-white shadow-sm flex-shrink-0"
                    alt="profile"
                    onError={(e) => {
                      e.target.src = DEFAULT_AVATAR;
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{savedAccount.name}</p>
                    <p className="text-xs text-gray-400">
                      {isQuickLoggingIn
                        ? "กำลังเข้าสู่ระบบ..."
                        : canSkipPassword
                          ? "แตะเพื่อเข้าสู่ระบบ ไม่ต้องกรอกรหัสผ่าน"
                          : "แตะเพื่อเข้าสู่ระบบด้วยบัญชีนี้"}
                    </p>
                  </div>
                  {isUploading || isQuickLoggingIn ? (
                    <BeatLoader size={6} color="#3b82f6" />
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-gray-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleForgetAccount}
                  disabled={isUploading || isQuickLoggingIn}
                  className="mt-2 text-xs text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
                >
                  ไม่ใช่บัญชีนี้ ลบออกจากเครื่อง
                </button>
              </div>
            )}
          </div>

          {/* ── RIGHT: Login form card ── */}
          <div className="flex-1 bg-white rounded-2xl shadow-sm border border-gray-100 p-8 flex flex-col justify-center">

            <div className="mb-7">
              <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">บัญชีผู้ใช้</p>
              <h2 className="text-xl font-bold text-gray-900">ยินดีต้อนรับกลับ</h2>
            </div>

            <form className="space-y-4" onSubmit={handleValidatePermission}>

              {/* Username */}
              <div>
                <label htmlFor="login-username" className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                  ชื่อผู้ใช้งาน
                </label>
                <input
                  id="login-username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="username"
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-blue-400 focus:bg-white transition-colors"
                />
              </div>

              {/* Password */}
              <div>
                <label htmlFor="login-password" className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                  รหัสผ่าน
                </label>
                <div className="relative">
                  <input
                    id="login-password"
                    name="password"
                    ref={passwordInputRef}
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="password"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-blue-400 focus:bg-white transition-colors pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2"
                  >
                    <img
                      src={
                        showPassword
                          ? "https://cdn-icons-png.flaticon.com/512/2767/2767194.png"
                          : "https://cdn-icons-png.flaticon.com/512/2767/2767146.png"
                      }
                      alt="toggle"
                      className="w-5 h-5 opacity-40 hover:opacity-70 transition-opacity"
                    />
                  </button>
                </div>
              </div>

              {/* Forgot password */}
              <div className="flex justify-end">
                <Link to="/reset_password" className="text-xs text-blue-600 font-medium hover:text-blue-700 transition-colors">
                  ลืมรหัสผ่าน?
                </Link>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isUploading}
                className="w-full py-3 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 active:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
              >
                {isUploading ? <BeatLoader size={8} color="#fff" /> : "เข้าสู่ระบบ"}
              </button>

              {/* Divider */}
              <div className="flex items-center gap-3 py-1">
                <div className="flex-1 h-px bg-gray-100" />
                <span className="text-xs text-gray-400">หรือ</span>
                <div className="flex-1 h-px bg-gray-100" />
              </div>

              {/* Register */}
              <Link to="/register">
                <div className="w-full py-3 bg-yellow-600 border border-yellow-600 text-white text-sm font-semibold text-center rounded-xl hover:bg-yellow-700 transition-colors">
                  สมัครสมาชิกใหม่
                </div>
              </Link>
            </form>

            <p className="text-xs text-gray-400 text-center mt-6 leading-relaxed">
              การเข้าสู่ระบบถือว่าคุณยอมรับ{" "}
              <span className="text-blue-600 cursor-pointer hover:underline">นโยบายความเป็นส่วนตัว</span>
              {" "}และ{" "}
              <span className="text-blue-600 cursor-pointer hover:underline">เงื่อนไขการใช้งาน</span>
            </p>
          </div>

        </div>
      </motion.div>
    </main>
  );
}
